import { AppBarEvents } from "@1js/office-start-appbar-events";
import AppBarIcons from "@1js/office-start-appbar-icons";
import { mergeClasses, tokens } from "@fluentui/react-components";
import {
  FluentIconsProps,
  ImageOff24Filled,
  ImageOff24Regular,
  ImageOff32Filled,
  ImageOff32Regular,
} from "@fluentui/react-icons";
import { useNovaEventing } from "@nova/react";
import React from "react";
import {
  FlyoutIconSize,
  FlyoutTileIconSize,
  OutlineIconFilterID,
  RadialGradientIconId,
  TileIconSizeDefault,
  TileIconSizeMercury,
  TileIconSizeWide,
  TileOutlineIconSizeWide,
  ViewMode,
  VisualRefresh,
} from "./Constants";
import { AppBarIconProps } from "./Icons";
import { useIconStyles } from "./TileIcon.styles";
import { TileIconProps } from "./TileIcon.types";
/**
 * Gets icon size based on the type of icon it is.
 * @param viewMode - The view mode of the icon (default App Bar, wide App Bar, flyout).
 * @param isOutlineIcon - Whether the icon is an outline icon.
 * @param isSmallColorIcon - Whether the icon is a small color icon.
 * @param isFullBleedIcon - Whether the icon is a full bleed icon.
 * @returns The CSS string for the dimensions of the icon.
 */
function getIconSize(
  viewMode: string,
  isOutlineIcon: boolean,
  isSmallColorIcon: boolean,
  isFullBleedIcon: boolean,
  enableMercuryDesignStyles: boolean,
  enableVisualRefreshStyles: boolean
) {
  let iconSize = TileIconSizeDefault;
  if (enableMercuryDesignStyles) {
    iconSize = TileIconSizeMercury;
  } else if (viewMode === ViewMode.Wide) {
    if (isOutlineIcon && enableVisualRefreshStyles) {
      iconSize = TileIconSizeWide;
    } else if (isOutlineIcon) {
      iconSize = TileOutlineIconSizeWide;
    } else if (isSmallColorIcon) {
      iconSize = TileIconSizeWide;
    } else {
      iconSize = TileIconSizeWide;
    }
  } else if (viewMode === ViewMode.Flyout) {
    if (isFullBleedIcon) {
      iconSize = FlyoutTileIconSize;
    } else {
      iconSize = FlyoutIconSize;
    }
  }
  return iconSize;
}
/**
 * Renders an icon for display in a LaunchTile
 *
 * @param icon: InternalIcon to render
 * @param isSelected: Used to determine correct coloring. True if the tile is selected. False otherwise.
 * @param label: Text to display underneath icon (OPTIONAL)
 * @returns
 */
export const TileIcon: React.FC<TileIconProps> = ({
  id,
  icon,
  isSelected,
  label,
  useHubColorForSelectedTileLabel,
  viewMode,
  brandingColor,
  showDotIndicator,
  useOutlineIconFilter,
  useSmallColorIconFilter,
  enableMercuryDesignStyles,
  enableVisualRefreshStyles,
  isGradientIconEnabled,
}) => {
  const [iconError, setIconError] = React.useState(false);
  const [retryCount, setRetryCount] = React.useState(0);
  const novaEventing = useNovaEventing();
  const onIconLoad = React.useCallback(() => {
    setIconError(false);
  }, []);
  const onIconError = React.useCallback(() => {
    setIconError(true);
    novaEventing.generateEvent({
      event: AppBarEvents.appIconError(() => {
        return {
          id,
          url:
            "__typename" in icon && icon.__typename === "RemoteImage"
              ? icon.src
              : "",
          error: "failed to load the image",
        };
      }),
    });
    // Retry loading the image after a delay
    if (retryCount < 10) {
      setTimeout(
        () => {
          setIconError(false); // Reset the error state to trigger a re-render and retry loading the image
          setRetryCount(retryCount + 1); // Increase the retry count
        },
        Math.pow(2, retryCount) * 1000
      ); // Delay retries by an exponentially increasing amount
    }
  }, [retryCount, icon, id, novaEventing]);
  const iconStyles = useIconStyles();
  const isFullBleedIcon =
    !useOutlineIconFilter &&
    !useSmallColorIconFilter &&
    icon != null &&
    "isFullBleed" in icon &&
    !!icon.isFullBleed;
  const iconSize = getIconSize(
    viewMode || ViewMode.Default,
    !!useOutlineIconFilter,
    !!useSmallColorIconFilter,
    isFullBleedIcon,
    !!enableMercuryDesignStyles,
    !!enableVisualRefreshStyles
  );
  const primaryFillColor = isSelected
    ? brandingColor || tokens.colorNeutralForeground3BrandSelected
    : viewMode === ViewMode.Flyout || !!enableMercuryDesignStyles
      ? tokens.colorNeutralForeground1
      : tokens.colorNeutralForeground3;
  const labelColor = isSelected
    ? useHubColorForSelectedTileLabel
      ? tokens.colorNeutralForeground3BrandSelected
      : tokens.colorNeutralForeground3Selected
    : viewMode === ViewMode.Flyout
      ? tokens.colorNeutralForeground1
      : tokens.colorNeutralForeground3;
  const FallBackIconComponent24 = isSelected
    ? (ImageOff24Filled as React.FC<
        React.HTMLAttributes<SVGElement> & FluentIconsProps & AppBarIconProps
      >)
    : (ImageOff24Regular as React.FC<
        React.HTMLAttributes<SVGElement> & FluentIconsProps & AppBarIconProps
      >);
  const FallBackIconComponent32 = isSelected
    ? (ImageOff32Filled as React.FC<
        React.HTMLAttributes<SVGElement> & FluentIconsProps & AppBarIconProps
      >)
    : (ImageOff32Regular as React.FC<
        React.HTMLAttributes<SVGElement> & FluentIconsProps & AppBarIconProps
      >);
  const FallBackIconComponent =
    viewMode == ViewMode.Flyout
      ? FallBackIconComponent32
      : FallBackIconComponent24;
  const outlineIconFilter = React.useMemo(() => {
    return (
      <svg className={iconStyles.outlineIconFilter}>
        <defs>
          <filter id={`${OutlineIconFilterID}-${id}`}>
            <feFlood
              floodColor={primaryFillColor}
              result="flood"
              className={iconStyles.floodFillIfHighContrast}
            />
            <feComposite
              in="SourceGraphic"
              in2="flood"
              operator="arithmetic"
              k1="1"
              k2="0"
              k3="0"
              k4="0"
            />
          </filter>
        </defs>
      </svg>
    );
  }, [
    iconStyles.outlineIconFilter,
    iconStyles.floodFillIfHighContrast,
    id,
    primaryFillColor,
  ]);
  const gradientIconSVGDef = React.useMemo(() => {
    return (
      <svg width="0" height="0">
        <defs>
          <radialGradient
            id={RadialGradientIconId}
            cx="0"
            cy="0"
            r="1"
            gradientUnits="userSpaceOnUse"
            gradientTransform={VisualRefresh.iconGradientTransform}
          >
            <stop
              offset="0.36721"
              stopColor={VisualRefresh.gradientStartColor}
            />
            <stop offset="0.975" stopColor={VisualRefresh.gradientEndColor} />
          </radialGradient>
        </defs>
      </svg>
    );
  }, []);
  const iconElement = React.useMemo(() => {
    if (!icon || iconError) {
      return (
        <FallBackIconComponent
          primaryFill={primaryFillColor}
          size={viewMode === ViewMode.Wide ? "wide" : "default"}
        />
      );
    }
    if (icon && !("__typename" in icon)) {
      return icon;
    }
    switch (icon.__typename) {
      case "InternalIcon": {
        let IconComponent = AppBarIcons[icon.name] as React.FC<
          React.HTMLAttributes<SVGElement> & FluentIconsProps & AppBarIconProps
        >;
        if (!IconComponent) {
          IconComponent = FallBackIconComponent;
        }
        return (
          <>
            <IconComponent
              primaryFill={
                isGradientIconEnabled && isSelected
                  ? `url(#${RadialGradientIconId})`
                  : primaryFillColor
              }
              size={
                viewMode === ViewMode.Wide || !!enableMercuryDesignStyles
                  ? "wide"
                  : "default"
              }
            />
            {isGradientIconEnabled && isSelected && gradientIconSVGDef}
          </>
        );
      }
      case "RemoteImage": {
        return (
          <>
            <img
              src={icon.src}
              alt={label}
              className={mergeClasses(
                isFullBleedIcon &&
                  viewMode === ViewMode.Flyout &&
                  iconStyles.flyoutIconImageFullBleed,
                isFullBleedIcon &&
                  viewMode !== ViewMode.Flyout &&
                  iconStyles.tileImageFullBleed
              )}
              style={{
                width: iconSize,
                height: iconSize,
                filter: useOutlineIconFilter
                  ? `url(#${`${OutlineIconFilterID}-${id}`})`
                  : undefined,
              }}
              data-clicklog="custom"
              onLoad={onIconLoad}
              onError={onIconError}
            />
            {useOutlineIconFilter && outlineIconFilter}
          </>
        );
      }
      default: {
        return undefined;
      }
    }
  }, [
    icon,
    iconError,
    FallBackIconComponent,
    primaryFillColor,
    viewMode,
    enableMercuryDesignStyles,
    label,
    isFullBleedIcon,
    iconStyles.flyoutIconImageFullBleed,
    iconStyles.tileImageFullBleed,
    iconSize,
    useOutlineIconFilter,
    useSmallColorIconFilter,
    id,
    onIconLoad,
    onIconError,
    outlineIconFilter,
    isGradientIconEnabled,
    gradientIconSVGDef,
    isSelected,
  ]);
  return (
    <div
      className={mergeClasses(
        iconStyles.tile,
        viewMode === ViewMode.Wide && iconStyles.wideTile,
        viewMode === ViewMode.Flyout && iconStyles.flyoutTile,
        enableMercuryDesignStyles && iconStyles.mercuryDesignTile
      )}
    >
      <div
        className={mergeClasses(
          viewMode === ViewMode.Flyout && iconStyles.flyoutIcon,
          viewMode === ViewMode.Flyout && iconStyles.flyoutIconMatted
        )}
      >
        {iconElement}
      </div>
      {viewMode !== ViewMode.Default && (
        <span
          className={mergeClasses(
            iconStyles.label,
            viewMode === ViewMode.Flyout && iconStyles.flyoutLabel
          )}
          style={{
            color: labelColor,
          }}
        >
          {showDotIndicator ? " \u25CF " : null}
          {label}
        </span>
      )}
    </div>
  );
};
